<template>
    <v-card class="overflow-hidden">
        <v-navigation-drawer
            v-model="showErwSuche"
            absolute
            bottom
            temporary
            right
            width="530px"
            color="kp-light-panel-bg"
        >
            <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                <v-spacer />
                <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
            </v-toolbar>
            <v-container fluid>
                <!-- Rollen -->
                <v-row dense>
                    <v-col cols="3" class="d-flex align-center">
                        <label class="tgk-paragraph-text">{{ $t('rolle') }}</label>
                    </v-col>
                    <v-col cols="9">
                        <v-select
                            v-model="extendedSearchParams.roles"
                            :items="roles.models"
                            item-text="label"
                            item-value="key"
                            clearable
                            :placeholder="$t('choose_multi')"
                            multiple
                        />
                    </v-col>
                </v-row>

                <!-- Erstellt -->
                <v-row dense>
                    <v-col cols="3" class="d-flex align-center">
                        <label class="tgk-paragraph-text">{{ $t('erstellt') }}</label>
                    </v-col>
                    <v-col cols="9" class="d-flex">
                        <DateTextfield
                            v-model="extendedSearchParams.created_date_from"
                            input-class="mr-1"
                            :placeholder="$t('von')"
                        />
                        <DateTextfield v-model="extendedSearchParams.created_date_to" :placeholder="$t('bis')" />
                    </v-col>
                </v-row>

                <!-- bearbeitet -->
                <v-row dense>
                    <v-col cols="3" class="d-flex align-center">
                        <label class="tgk-paragraph-text">{{ $t('bearbeitet') }}</label>
                    </v-col>
                    <v-col cols="9" class="d-flex">
                        <DateTextfield
                            v-model="extendedSearchParams.modified_date_from"
                            input-class="mr-1"
                            :placeholder="$t('von')"
                        />
                        <DateTextfield v-model="extendedSearchParams.modified_date_to" :placeholder="$t('bis')" />
                    </v-col>
                </v-row>

                <!-- Kulturverbund -->
                <v-row dense>
                    <v-col cols="3" class="d-flex align-center">
                        <label class="tgk-paragraph-text">{{ $t('kulturverbund') }}</label>
                    </v-col>
                    <v-col cols="9">
                        <v-radio-group v-model="extendedSearchParams.in_organizer_group" row>
                            <v-radio value="" :label="$t('egal')" />
                            <v-radio value="true" :label="$t('yes')" />
                            <v-radio value="false" :label="$t('no')" />
                        </v-radio-group>
                    </v-col>
                </v-row>

                <v-row dense>
                    <v-col class="d-flex justify-end">
                        <PlainBtn @click="resetEnhancedSearch">{{ $t('reset_search') }}</PlainBtn>
                        <ActionBtn class="ml-1" @click="onEnhancedSearch">
                            <KpIcon icon-cls="searchmagnifier" color="white" left />
                            {{ $t('search') }}
                        </ActionBtn>
                    </v-col>
                </v-row>
            </v-container>
        </v-navigation-drawer>
        <BaseRemoteList
            ref="userList"
            :session-key="sessionKey"
            :maintitle="$t('benutzer')"
            :collection-class="UserCollection"
            :headers="headers"
            :filters="filters"
            :extra-params="extraParams"
            :options="{ sortBy: ['username'] }"
            xlsx-export
            :height="height"
            @loading="loading = $event"
            @edit-item="editRecord"
            @new-item="newRecord"
            @dblclick:row="onRowDblClick"
        >
            <template #toolbar-items>
                |
                <v-btn-toggle v-model="activeFilter" class="ml-2" borderless tile background-color="primary">
                    <IconBtn
                        value="active"
                        color="primary"
                        :disabled="loading"
                        icon-cls="markedevents"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('aktiv')"
                    />
                    <IconBtn
                        value="inactive"
                        color="primary"
                        :disabled="loading"
                        icon-cls="checkboxunchecked"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('inaktiv')"
                    />
                </v-btn-toggle>
            </template>
            <template #after-query-field>
                <div
                    :class="{
                        'ml-1': true,
                        'erw-suche-indicator': showEnhancedSearchIndicator,
                    }"
                >
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="advancedsearch"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('erw_suche')"
                        @click="showErwSuche = true"
                    />
                </div>
            </template>
            <template #item.context_menu="{ item }">
                <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                        <v-btn dark icon v-bind="attrs" v-on="on">
                            <KpIcon icon-cls="moredots" color="grey" />
                        </v-btn>
                    </template>

                    <UserContextMenu
                        :record="item"
                        @delete-record="(record) => $refs.userList.initiateDelete(record)"
                        @reload-list="() => $refs.userList.reload()"
                        @duplicate-record="duplicateUser($event)"
                    />
                </v-menu>
            </template>
            <template #item.active="{ item }">
                <TrueFalseIcon :active="item.active" />
            </template>
            <template #item.created="{ item }">
                {{ item.created.formatIf('dd, DD.MM.YYYY HH:mm') }}
            </template>
            <template #item.modified="{ item }">
                {{ item.modified.formatIf('dd, DD.MM.YYYY HH:mm') }}
            </template>
            <template #item.last_login="{ item }">
                {{ item.last_login.formatIf('dd, DD.MM.YYYY HH:mm') }}
            </template>
        </BaseRemoteList>
    </v-card>
</template>

<script>
import { UserCollection } from '@/models/User';
import sessionStorageMixin from '@/mixins/session-storage';
import { getRoles } from '@/models/Role';
import UserContextMenu from '@/components/user/ContextMenu.vue';

export default {
    name: 'UserIndex',
    components: {
        UserContextMenu,
    },
    mixins: [sessionStorageMixin],
    props: {
        sessionKey: {
            type: String,
            required: true,
        },
        height: {
            type: [String, Number],
            default: null,
        },
    },

    data() {
        return {
            loading: false,
            UserCollection,
            roles: getRoles(),
            headers: [
                { width: 35, sortable: false, value: 'context_menu' },
                { text: 'ID', sortable: true, value: '$.id', width: 65 },
                {
                    text: this.$t('aktiv'),
                    sortable: true,
                    width: 80,
                    align: 'center',
                    value: 'active',
                },
                { text: this.$t('username'), sortable: true, width: 160, value: '$.username' },
                { text: this.$t('nachname'), sortable: true, width: 180, value: '$.lastname' },
                { text: this.$t('vorname'), sortable: true, width: 180, value: '$.firstname' },
                { text: this.$t('event_organizer'), sortable: true, width: 160, value: '$.title' },
                { text: this.$t('role_name'), sortable: true, width: 100, value: '.roleName' },
                { text: this.$t('kulturverbund'), sortable: true, width: 100, value: '$.organizer_group' },
                { text: this.$t('created'), sortable: true, width: 100, value: 'created' },
                { text: this.$t('modified'), sortable: true, width: 100, value: 'modified' },
                { text: this.$t('last_login'), sortable: true, width: 100, value: 'last_login' },
            ],
            activeFilter: this.getSessionValue('activeFilter') || null,

            showErwSuche: false,
            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},

            // will be set when extended search btn is clicked --> initially taken from extended SearchParams
            enhancedFilters: this.getSessionValue('extendedSearchParams') || null,
        };
    },

    computed: {
        filters() {
            const filters = [];
            if (this.activeFilter === 'active') {
                filters.push({ property: 'active', value: true });
            }
            if (this.activeFilter === 'inactive') {
                filters.push({ property: 'active', value: false });
            }
            return filters;
        },

        extraParams() {
            const extraParams = {};
            const enhancedFilters = {};

            for (let key in this.enhancedFilters) {
                if (this.enhancedFilters[key]) {
                    enhancedFilters[key] = this.enhancedFilters[key];
                }
            }
            extraParams.enhancedFilters = enhancedFilters;
            return extraParams;
        },

        showEnhancedSearchIndicator() {
            let show = false;
            for (const key in this.enhancedFilters || {}) {
                if (!Array.isArray(this.enhancedFilters[key]) || this.enhancedFilters[key].length > 0) {
                    show = true;
                }
            }
            return show;
        },
    },

    watch: {
        activeFilter(activeFilter) {
            this.setSessionValue('activeFilter', activeFilter);
        },
    },

    methods: {
        onRowDblClick(e, data) {
            if (data.item) {
                this.editRecord(data.item);
            }
        },

        editRecord(user) {
            this.$router.push({ name: 'user-edit', params: { id: user.id } });
        },

        newRecord() {
            this.$router.push({ name: 'user-edit', params: { id: 'new' } });
        },
        onEnhancedSearch() {
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
        },
    },
};
</script>
